import React, { useState, useCallback, useRef } from "react";

function CropOverlay({ element, canvasWidth, canvasHeight, onConfirm, onCancel }) {
  const [selection, setSelection] = useState(null); // { x, y, w, h } in element-local coords
  const [drawing, setDrawing] = useState(false);
  const startRef = useRef(null);

  const handleMouseDown = useCallback(
    (e) => {
      e.stopPropagation();
      e.preventDefault();
      const rect = e.currentTarget.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      startRef.current = { x, y };
      setSelection({ x, y, w: 0, h: 0 });
      setDrawing(true);

      const handleMouseMove = (moveE) => {
        const mx = moveE.clientX - rect.left;
        const my = moveE.clientY - rect.top;
        const sx = Math.max(0, Math.min(startRef.current.x, mx));
        const sy = Math.max(0, Math.min(startRef.current.y, my));
        const ex = Math.min(element.width, Math.max(startRef.current.x, mx));
        const ey = Math.min(element.height, Math.max(startRef.current.y, my));
        setSelection({ x: sx, y: sy, w: ex - sx, h: ey - sy });
      };

      const handleMouseUp = () => {
        setDrawing(false);
        window.removeEventListener("mousemove", handleMouseMove);
        window.removeEventListener("mouseup", handleMouseUp);
      };

      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
    },
    [element.width, element.height]
  );

  const handleConfirm = useCallback(() => {
    if (!selection || selection.w < 5 || selection.h < 5) return;

    // Load image to get natural dimensions for coordinate mapping
    const img = new Image();
    img.onload = () => {
      const scaleX = img.naturalWidth / element.width;
      const scaleY = img.naturalHeight / element.height;
      const sx = Math.round(selection.x * scaleX);
      const sy = Math.round(selection.y * scaleY);
      const sw = Math.round(selection.w * scaleX);
      const sh = Math.round(selection.h * scaleY);
      onConfirm(element.id, sx, sy, sw, sh);
    };
    img.src = element.url;
  }, [selection, element, onConfirm]);

  return (
    <div
      style={{
        position: "absolute",
        left: element.x,
        top: element.y,
        width: element.width,
        height: element.height,
        zIndex: 9999,
        cursor: "crosshair",
      }}
      onMouseDown={handleMouseDown}
    >
      {/* Dark mask with cutout */}
      {selection && selection.w > 0 && selection.h > 0 && (
        <>
          {/* Top */}
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: selection.y,
              background: "rgba(0,0,0,0.5)",
              pointerEvents: "none",
            }}
          />
          {/* Bottom */}
          <div
            style={{
              position: "absolute",
              top: selection.y + selection.h,
              left: 0,
              width: "100%",
              height: element.height - selection.y - selection.h,
              background: "rgba(0,0,0,0.5)",
              pointerEvents: "none",
            }}
          />
          {/* Left */}
          <div
            style={{
              position: "absolute",
              top: selection.y,
              left: 0,
              width: selection.x,
              height: selection.h,
              background: "rgba(0,0,0,0.5)",
              pointerEvents: "none",
            }}
          />
          {/* Right */}
          <div
            style={{
              position: "absolute",
              top: selection.y,
              left: selection.x + selection.w,
              width: element.width - selection.x - selection.w,
              height: selection.h,
              background: "rgba(0,0,0,0.5)",
              pointerEvents: "none",
            }}
          />
          {/* Selection border */}
          <div
            style={{
              position: "absolute",
              left: selection.x,
              top: selection.y,
              width: selection.w,
              height: selection.h,
              border: "2px dashed #00a4e4",
              pointerEvents: "none",
              boxSizing: "border-box",
            }}
          />
        </>
      )}

      {/* Confirm/Cancel buttons */}
      {selection && selection.w > 5 && selection.h > 5 && !drawing && (
        <div
          style={{
            position: "absolute",
            left: selection.x + selection.w / 2,
            top: selection.y + selection.h + 8,
            transform: "translateX(-50%)",
            display: "flex",
            gap: 8,
            zIndex: 10000,
          }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            onClick={handleConfirm}
            style={{
              padding: "4px 12px",
              background: "#00a4e4",
              color: "#fff",
              border: "none",
              borderRadius: 0,
              cursor: "pointer",
              fontSize: 12,
              fontWeight: 600,
            }}
          >
            Confirm
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onCancel();
            }}
            style={{
              padding: "4px 12px",
              background: "#fff",
              color: "#333",
              border: "1px solid #b8b2a6",
              borderRadius: 0,
              cursor: "pointer",
              fontSize: 12,
            }}
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}

export default CropOverlay;
